/**
 * Floating capture button with gesture-based mode switching.
 * - Manual mode: Camera icon, tap to capture, long press enters auto mode
 * - Auto mode: Play/Pause buttons, long press returns to manual mode
 * @module FloatingCaptureButton
 */

import { Camera, Pause, Play, Video } from 'lucide-react';
import { useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

const SERVICE_TAG = '[FLOATING_CAPTURE]';

/** Duration in ms to trigger long press */
const LONG_PRESS_MS = 500;

/**
 * Props for FloatingCaptureButton component.
 */
export interface FloatingCaptureButtonProps {
  /** Current capture mode */
  mode: 'manual' | 'auto';
  /** Auto-capture state when in auto mode */
  autoState: 'running' | 'paused';
  /** Callback when manual capture is triggered (tap in manual mode) */
  onCapture: () => void;
  /** Callback to enter auto mode (long press in manual mode) */
  onEnterAutoMode: () => void;
  /** Callback to exit auto mode (long press in auto mode) */
  onExitAutoMode: () => void;
  /** Callback to pause auto-capture */
  onPause: () => void;
  /** Callback to resume auto-capture */
  onResume: () => void;
  /** Whether button is disabled */
  disabled?: boolean;
  /**
   * Visual hint for what a tap will do in `mode === 'manual'` (TICKET-299).
   * Defaults to `'photo'` — swaps the icon to a Video glyph when set to `'video'`.
   * No behaviour change in this component: the parent's `onCapture` callback
   * decides whether to fire a photo or a video capture.
   */
  captureKind?: 'photo' | 'video';
}

/**
 * Floating capture button overlaid on camera view.
 * Supports gesture-based mode switching between manual and auto capture.
 */
export function FloatingCaptureButton({
  mode,
  autoState,
  onCapture,
  onEnterAutoMode,
  onExitAutoMode,
  onPause,
  onResume,
  disabled = false,
  captureKind = 'photo'
}: FloatingCaptureButtonProps) {
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  /**
   * Clear any pending long press timer.
   */
  const clearPressTimer = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  /**
   * Handle pointer down for manual mode capture button.
   * Starts long press timer for entering auto mode.
   */
  const handleManualPointerDown = useCallback(() => {
    if (disabled) return;
    didLongPress.current = false;

    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      console.info(`${SERVICE_TAG} Long press detected, entering auto mode`);
      onEnterAutoMode();
    }, LONG_PRESS_MS);
  }, [disabled, onEnterAutoMode]);

  /**
   * Handle pointer up for manual mode capture button.
   * If not a long press, trigger capture.
   */
  const handleManualPointerUp = useCallback(() => {
    clearPressTimer();

    if (!didLongPress.current && !disabled) {
      onCapture();
    }
  }, [clearPressTimer, disabled, onCapture]);

  /**
   * Handle pointer down for auto mode buttons.
   * Starts long press timer for exiting auto mode.
   */
  const handleAutoPointerDown = useCallback(() => {
    if (disabled) return;
    didLongPress.current = false;

    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      console.info(`${SERVICE_TAG} Long press detected, exiting auto mode`);
      onExitAutoMode();
    }, LONG_PRESS_MS);
  }, [disabled, onExitAutoMode]);

  /**
   * Handle pointer up for pause button.
   * If not a long press, trigger pause.
   */
  const handlePausePointerUp = useCallback(() => {
    clearPressTimer();

    if (!didLongPress.current && !disabled) {
      onPause();
    }
  }, [clearPressTimer, disabled, onPause]);

  /**
   * Handle pointer up for play button.
   * If not a long press, trigger resume.
   */
  const handlePlayPointerUp = useCallback(() => {
    clearPressTimer();

    if (!didLongPress.current && !disabled) {
      onResume();
    }
  }, [clearPressTimer, disabled, onResume]);

  /**
   * Handle pointer leave/cancel - cleanup timer.
   */
  const handlePointerLeave = useCallback(() => {
    clearPressTimer();
    didLongPress.current = false;
  }, [clearPressTimer]);

  // Base button styles — lily pad: green radial shadow, subtle ring, water ripple on press
  const baseButtonClass = cn(
    'flex items-center justify-center rounded-full transition-all',
    'bg-white/30 backdrop-blur-sm border border-white/20',
    // Lily-pad: thin green ring edge + radial green glow
    'ring-2 ring-primary/40 shadow-[0_4px_16px_hsl(95_64%_46%/0.35)]',
    // Water ripple on press: box-shadow expands and fades
    'active:scale-95 active:bg-white/40 active:shadow-[0_0_0_12px_hsl(95_64%_46%/0.15),0_4px_16px_hsl(95_64%_46%/0.2)]',
    'touch-none select-none',
    disabled && 'opacity-50 pointer-events-none'
  );

  // Manual mode: Single capture button
  if (mode === 'manual') {
    const ManualIcon = captureKind === 'video' ? Video : Camera;
    const ariaLabel =
      captureKind === 'video'
        ? 'Record video. Hold to enable auto-capture mode.'
        : 'Capture image. Hold to enable auto-capture mode.';
    return (
      <button
        type="button"
        className={cn(baseButtonClass, 'w-16 h-16')}
        onPointerDown={handleManualPointerDown}
        onPointerUp={handleManualPointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
        disabled={disabled}
        aria-label={ariaLabel}
        data-capture-kind={captureKind}
      >
        <ManualIcon className="w-8 h-8 text-white drop-shadow-md" />
      </button>
    );
  }

  // Auto mode: Play and Pause buttons
  return (
    <div className="flex items-center gap-4">
      {/* Pause button */}
      <button
        type="button"
        className={cn(
          baseButtonClass,
          'w-14 h-14',
          autoState === 'paused' && 'bg-white/50 border-white/40'
        )}
        onPointerDown={handleAutoPointerDown}
        onPointerUp={handlePausePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
        disabled={disabled}
        aria-label="Pause auto-capture. Hold to return to manual mode."
        aria-pressed={autoState === 'paused'}
      >
        <Pause
          className={cn(
            'w-6 h-6 drop-shadow-md',
            autoState === 'paused' ? 'text-white' : 'text-white/70'
          )}
        />
      </button>

      {/* Play button */}
      <button
        type="button"
        className={cn(
          baseButtonClass,
          'w-14 h-14',
          autoState === 'running' && 'bg-white/50 border-white/40'
        )}
        onPointerDown={handleAutoPointerDown}
        onPointerUp={handlePlayPointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
        disabled={disabled}
        aria-label="Resume auto-capture. Hold to return to manual mode."
        aria-pressed={autoState === 'running'}
      >
        <Play
          className={cn(
            'w-6 h-6 drop-shadow-md',
            autoState === 'running' ? 'text-white' : 'text-white/70'
          )}
        />
      </button>
    </div>
  );
}
